import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { useAppSelector } from "../../store/hook";
import {
  buildServiceRequestPrefill,
  serviceRequestUrl,
} from "../../hooks/serviceRequestFrame";
import { useServiceRequestFrame } from "../../hooks/useServiceRequestFrame";

// If the form never says it loaded (blocked frame, network), offer the other ways in.
export const LOAD_TIMEOUT_MS = 15000;

type Status = "loading" | "ready" | "done" | "failed";

const CUSTOMER_CARE_PHONE = "(833) 500-0014";
const CUSTOMER_CARE_EMAIL = "customerservice@gofreedompower.com";

export const ServiceRequestDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const portal = useAppSelector((state) => state.portal || ({} as any));
  const customerId = useAppSelector((state) => state.me?.customerId);
  const prefill = useMemo(
    () => buildServiceRequestPrefill(portal, customerId),
    [portal, customerId]
  );
  const url = useMemo(() => serviceRequestUrl(), []);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState<Status>("loading");

  // A fresh form each time the modal opens.
  useEffect(() => {
    if (open) setStatus("loading");
  }, [open]);

  useEffect(() => {
    if (!open || status !== "loading") return;
    const timer = setTimeout(() => setStatus("failed"), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [open, status]);

  const onReady = useCallback(() => {
    setStatus((s) => (s === "loading" || s === "failed" ? "ready" : s));
  }, []);
  const onDone = useCallback(() => setStatus("done"), []);

  useServiceRequestFrame({
    iframeRef,
    url,
    prefill,
    active: open && status !== "done",
    onReady,
    onDone,
  });

  // Kept (hidden) while the fallback shows, so a form that loads late still takes over.
  const showFrame = status !== "done";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      aria-labelledby="service-request-title"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: { xs: "100dvh", sm: "min(90vh, 860px)" },
          borderRadius: { xs: 0, sm: "8px" },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          flex: "none",
          paddingX: { xs: 4, sm: 6 },
          paddingY: 3,
          paddingTop: {
            xs: "calc(12px + env(safe-area-inset-top))",
            sm: 3,
          },
          borderBottom: "1px solid #E8EBED",
        }}
      >
        <Typography
          id="service-request-title"
          variant="subtitle1"
          fontWeight={600}
          color="textPrimary"
        >
          Submit a service request
        </Typography>
        <IconButton aria-label="close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          position: "relative",
          flex: "auto",
          minHeight: 0,
          paddingBottom: { xs: "env(safe-area-inset-bottom)", sm: 0 },
        }}
      >
        {showFrame && (
          <Box
            component="iframe"
            ref={iframeRef}
            src={url}
            title="Service request form"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              border: 0,
              visibility: status === "ready" ? "visible" : "hidden",
            }}
          />
        )}

        {status === "loading" && (
          <Stack
            role="status"
            alignItems="center"
            justifyContent="center"
            gap={3}
            sx={{ position: "absolute", inset: 0 }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading the form...
            </Typography>
          </Stack>
        )}

        {status === "done" && (
          <Stack
            role="status"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            gap={4}
            sx={{ height: "100%", paddingX: 6 }}
          >
            <CheckCircleRoundedIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" fontWeight={600} color="textPrimary">
              Thanks, our service team will reach out.
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              We received your request and will contact you using the details
              you gave us.
            </Typography>
            <Button variant="contained" size="large" onClick={onClose}>
              Done
            </Button>
          </Stack>
        )}

        {status === "failed" && (
          <Stack
            role="alert"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            gap={4}
            sx={{ position: "absolute", inset: 0, paddingX: 6 }}
          >
            <Typography variant="h6" fontWeight={600} color="textPrimary">
              We couldn't load the form.
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Please call Customer Care at{" "}
              <Link href={`tel:${CUSTOMER_CARE_PHONE}`}>
                {CUSTOMER_CARE_PHONE}
              </Link>{" "}
              or email{" "}
              <Link href={`mailto:${CUSTOMER_CARE_EMAIL}`}>
                {CUSTOMER_CARE_EMAIL}
              </Link>
              , or use the{" "}
              <Link
                href="https://www.gofreedompower.com/contact"
                target="_blank"
                rel="noopener"
              >
                form on our website
              </Link>
              .
            </Typography>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};
